import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import {
  Column,
  COMMON_CONSTANTS,
  useProduct,
  getCurrentUserHook
} from '@nile/components';
import { SVGIcon } from '@superops/super-icons';
import { USER_GUIDE } from '../Constants';
import { FormattedMessage } from 'react-intl';
import { DisplayText } from '@superops/ui-design-system';
import { SizeMe } from 'react-sizeme';
import { CustomIframe } from './CustomIframe';

import {
  AccountClient,
  SAVE_PERSONALIZATION,
  GET_PERSONALIZATION_BY_NAME
} from '@nile/graphql';
import { useMutation, useLazyQuery } from '@apollo/client';
import { ContractContext } from '@nile/client_management/src/Contracts/Contexts/ContractContext';

const SCREEN_LIMIT = 1500;

interface IUserGuideContext {
  open: () => void;
  close: () => void;
  isVisible: boolean;
  setIsVisible?: React.Dispatch<boolean>;
  toggle: () => void;
  openModal: () => void;
  closeModal: () => void;
}

interface Offset {
  top?: React.CSSProperties['top'];
  left?: React.CSSProperties['left'];
  right?: React.CSSProperties['right'];
  bottom?: React.CSSProperties['bottom'];
}

const UserGuideContext = createContext<null | IUserGuideContext>(null);

export function useUserGuideContext(): IUserGuideContext {
  const context = useContext(UserGuideContext);

  if (context === null) {
    throw Error(
      'Please use `useUserGuideContext` inside the children of `UserGuide` component'
    );
  }

  return context;
}

function UserGuide({
  children,
  persist,
  module
}: {
  children: React.ReactNode;
  persist: boolean;
  module: string;
}) {
  const isMSPProduct = useProduct(COMMON_CONSTANTS.MSP);
  const isShowModalScreen = window.innerWidth >= SCREEN_LIMIT;
  const [isVisible, setIsModalVisibility] = useState(!!isMSPProduct);

  useEffect(() => {
    if (isMSPProduct) setIsModalVisibility(window.innerWidth >= SCREEN_LIMIT);
  }, [isMSPProduct]);

  useEffect(() => {
    if (persist) {
      getModulePersonalisation();
    }
  }, [persist]);

  const { contractType } = useContext(ContractContext);
  const type =
    contractType === 'SERVICE'
      ? USER_GUIDE.RECURRING_SERVICE_CONTRACT
      : (contractType as USER_GUIDE);
  const user = getCurrentUserHook();
  const personalisationRef = useRef(null);

  const [
    getModulePersonalisation,
    { data: personalisedColumns, loading, error }
  ] = useLazyQuery(GET_PERSONALIZATION_BY_NAME, {
    client: AccountClient,
    variables: {
      userId: user?.userId,
      category: module,
      name: `${module}-Personalisation`
    },
    onCompleted({ getPersonalizationByCategoryAndName }) {
      if (!getPersonalizationByCategoryAndName?.value) {
        setIsModalVisibility(window.innerWidth >= SCREEN_LIMIT);
        return;
      }
      const parsedValue = JSON.parse(getPersonalizationByCategoryAndName.value);
      const isShowModalPersonalize =
        parsedValue.find((item) => item.id === type)?.isShowModal ??
        window.innerWidth >= SCREEN_LIMIT;
      setIsModalVisibility(isShowModalPersonalize);
    }
  });

  const [savePersonalization] = useMutation(SAVE_PERSONALIZATION, {
    client: AccountClient,
    update(cache, { data }) {
      cache.modify({
        fields: {
          getPersonalizationByCategoryAndName(existingData = {}) {
            // Append if no existing data, otherwise update if name and category match
            if (!existingData || Object.keys(existingData).length === 0) {
              return {
                name: `${module}-Personalisation`,
                category: module,
                value: JSON.stringify(personalisationRef.current)
              };
            }

            if (
              existingData?.name === `${module}-Personalisation` &&
              existingData?.category === module
            ) {
              return {
                ...existingData,
                value: JSON.stringify(personalisationRef.current)
              };
            }
            return existingData; // Keep unchanged if no match
          }
        }
      });
    }
  });

  const updatePersonalization = (showModal: boolean) => {
    const rawValue =
      personalisedColumns?.getPersonalizationByCategoryAndName?.value;
    const columnPersonalisation = rawValue ? JSON.parse(rawValue) : [];

    const existingItem = columnPersonalisation.find((item) => item.id === type);
    const updatevalue = existingItem
      ? columnPersonalisation.map((item) => ({
          ...item,
          isShowModal: item.id === type ? showModal : item.isShowModal
        }))
      : [...columnPersonalisation, { id: type, isShowModal: showModal }];

    personalisationRef.current = updatevalue;
    return savePersonalization({
      variables: {
        user: {
          firstName: user?.firstName,
          userId: user?.userId,
          email: user?.email,
          lastName: user?.lastName
        },
        category: module,
        name: `${module}-Personalisation`,
        value: JSON.stringify(updatevalue)
      }
    });
  };

  const handleOpenModal = async () => {
    if (module) {
      updatePersonalization(true); // pass the isshowmodal value for savepersonalization
    }
    setIsModalVisibility(true);
  };

  const handleCloseModal = async () => {
    if (module) {
      updatePersonalization(false); // pass the isshowmodal value for savepersonalization
    }
    setIsModalVisibility(false);
  };

  return (
    <UserGuideContext.Provider
      value={{
        open: () => setIsModalVisibility(true),
        close: () => setIsModalVisibility(false),
        toggle: () => setIsModalVisibility((c) => !c),
        setIsVisible: setIsModalVisibility,
        isVisible,
        openModal: handleOpenModal,
        closeModal: handleCloseModal
      }}
    >
      {children}
    </UserGuideContext.Provider>
  );
}

const CONTENT_MAP = {
  [USER_GUIDE.SERVICE]: {
    content: 'msp.contract.userGuide.service',
    video: 'https://www.youtube.com/embed/YMFwy37A0Wg?si=16299lEHkoh7Dxft'
  },
  [USER_GUIDE.PRODUCT]: {
    content: 'msp.contract.userGuide.product',
    video: 'https://www.youtube.com/embed/YMFwy37A0Wg?si=16299lEHkoh7Dxft'
  },
  [USER_GUIDE.PACKAGE]: {
    content: 'msp.contract.userGuide.package',
    video: 'https://www.youtube.com/embed/YMFwy37A0Wg?si=16299lEHkoh7Dxft'
  },
  [USER_GUIDE.RECURRING_SERVICE_CONTRACT]: {
    hasSubType: true,
    BASIC: { content: 'msp.contract.userGuide.recurringService' },
    APPLICATION: {
      content: 'msp.contract.userGuide.recurringService.application'
    },
    video: 'https://www.youtube.com/embed/qAefTOWaBLg?si=5qFd3xMERseRlcGt'
  },
  [USER_GUIDE.ONE_TIME_CONTRACT]: {
    hasSubType: true,
    BASIC: { content: 'msp.contract.userGuide.oneTime' },
    APPLICATION: { content: 'msp.contract.userGuide.oneTime.application' },
    video: 'https://www.youtube.com/embed/Nwo_x4_Fokk?si=24qsdA0Ndn5F025j'
  },
  [USER_GUIDE.USAGE_BASED_CONTRACT]: {
    content: 'msp.contract.userGuide.usageBased',
    video: 'https://www.youtube.com/embed/OydGORi0juE?si=9bu54dxOdDSdsTAU'
  },
  [USER_GUIDE.TIME_AND_MATERIAL_CONTRACT]: {
    content: 'msp.contract.userGuide.timeAndMaterial',
    video: 'https://www.youtube.com/embed/pqXGNIFjGuY?si=HmanSMiXkf7nuEZ7'
  }
};

const ModalWrapper = styled.div<{
  offset: Offset;
  isRenderedAsModal: boolean;
}>`
  ${(props) => {
    if (props.isRenderedAsModal) {
      return `
      position: absolute;
      right: ${props.offset?.right ?? '40px'};
      top: ${props.offset?.top ?? 0};
      left: ${props.offset?.left};
      bottom: ${props.offset?.bottom};
      width: 400px;
      `;
    }

    return `
    position: relative;
    margin-right: 40px;
    margin-top: ${props.offset?.top};
    margin-left: 10px;
    width: 30%;
    `;
  }}

  background: #fff;
  max-height: 80%;
  overflow-y: auto;
  padding: 20px;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.2);
  z-index: 200;
  height: fit-content;

  .close-button {
    position: absolute;
    right: 20px;
    top: 20px;
  }
`;

function UserGuidePopupSheet({
  type,
  offset,
  isBasic,
  size
}: {
  type: USER_GUIDE;
  offset: Offset;
  isBasic: boolean;
  size: {
    height: number | null;
    width: number | null;
  };
}) {
  const { closeModal } = useUserGuideContext();

  const [isSmallScreen, setIsSmallScreen] = useState(false);

  const user = getCurrentUserHook();

  useEffect(() => {
    if (!size.width) {
      return;
    }
    setIsSmallScreen(window.innerWidth < SCREEN_LIMIT);
  }, [size.width]);

  if (!type) return null;

  return (
    <ModalWrapper
      offset={offset}
      isRenderedAsModal={isSmallScreen}
      className="user-guide section2"
    >
      <Column>
        <Column>
          <div className="close-button cursor-pointer" onClick={closeModal}>
            <SVGIcon name="Close" />
          </div>
          <FormattedMessage
            id={
              CONTENT_MAP[type]?.hasSubType
                ? `${
                    CONTENT_MAP[type][isBasic ? 'BASIC' : 'APPLICATION'].content
                  }.new`
                : `${CONTENT_MAP[type]?.content}.new`
            }
            values={{
              ul: (children) => (
                <ul className="pl-15 m0imp pb10">{children}</ul>
              ),
              ol: (children) => (
                <ol className="pl-15 m0imp pb10">{children}</ol>
              ),
              li: (children) => (
                <li>
                  <DisplayText element="p" size="medium">
                    {children}
                  </DisplayText>
                </li>
              ),
              h1: (text) => {
                return (
                  <div className="mb-10">
                    <DisplayText
                      element="div"
                      size="extraLarge"
                      weight="SemiBold"
                    >
                      {text}
                    </DisplayText>
                  </div>
                );
              },
              h2: (text) => {
                return (
                  <div className="mb-5">
                    <DisplayText element="div" size="large" weight="SemiBold">
                      {text}
                    </DisplayText>
                  </div>
                );
              },
              p: (text) => {
                return (
                  <div className="mb-10">
                    <DisplayText element="p" size="medium">
                      {text}
                    </DisplayText>
                  </div>
                );
              },
              br: () => <br />
            }}
          />
        </Column>

        <CustomIframe videoId={CONTENT_MAP[type]?.video} />
      </Column>
    </ModalWrapper>
  );
}

function UserGuidePopup({
  children,
  ...props
}: PropsWithChildren<{
  type: USER_GUIDE;
  offset: Offset;
  isBasic: boolean;
}>) {
  const containerRef = useRef(null);
  const [, forceUpdate] = useState({});

  useEffect(() => {
    forceUpdate({});
  }, []);

  const { isVisible } = useUserGuideContext();

  return (
    <div
      className="position-relative full-height full-width fx"
      ref={containerRef}
    >
      <div className="user-guide section1">{children}</div>
      {isVisible && (
        <SizeMe monitorWidth>
          {({ size }) => {
            if (window.innerWidth < SCREEN_LIMIT) {
              return (
                containerRef.current &&
                createPortal(
                  <UserGuidePopupSheet {...props} size={size} />,
                  containerRef.current
                )
              );
            }
            return <UserGuidePopupSheet {...props} size={size} />;
          }}
        </SizeMe>
      )}
    </div>
  );
}

UserGuide.Popup = UserGuidePopup;

export { UserGuide };
